import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  Clock, 
  Users, 
  Gem, 
  Mountain, 
  Star, 
  ArrowRight, 
  DollarSign,
  Info,
  AlertCircle,
  CheckCircle,
  Camera,
  Shield,
  Thermometer
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import { tours } from "../data/mock";

const UndergroundToursPage = () => {
  const [tourBookingEnabled, setTourBookingEnabled] = useState(true);
  const [tourSchedule, setTourSchedule] = useState({ restrict_monday_friday: true });
  const [openingHours, setOpeningHours] = useState({ closed_on_tuesdays: false });

  useEffect(() => {
    checkTourBookingStatus();
    fetchTourSchedule();
    fetchOpeningHours();
  }, []);

  const checkTourBookingStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/features/public`);
      if (response.ok) {
        const data = await response.json();
        setTourBookingEnabled(!data.store_maintenance_mode); // Assume tours available when store not in maintenance
      }
    } catch (error) {
      console.error('Error checking tour booking status:', error);
    }
  };

  const fetchTourSchedule = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/tour-schedule/public`);
      if (response.ok) {
        const data = await response.json();
        setTourSchedule(data);
      }
    } catch (error) {
      console.error('Error fetching tour schedule:', error);
    }
  };

  const fetchOpeningHours = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/opening-hours/public`);
      if (response.ok) {
        const data = await response.json();
        setOpeningHours(data);
      }
    } catch (error) {
      console.error('Error fetching opening hours:', error);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Underground Opal Mine Tours - Umoona",
    "description": "Authentic underground opal mine tours in Coober Pedy. Experience real mining tunnels, learn about opal formation, and discover Australian mining heritage.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy",
      "addressRegion": "SA",
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    "openingHours": "Mo-Su 08:30-17:30",
    "priceRange": "$8.50-$36.00",
    "url": "https://umoonaopalmine.com.au/underground-tours"
  };

  return (
    <div>
      <SEOHead
        title="Underground Opal Mine Tours | Umoona Opal Mine Coober Pedy"
        description="Experience real underground opal mine tours in Coober Pedy. Descend into Umoona Mine, discover opal mining history, and see where the world's finest opals are found."
        keywords="underground tours, opal mine tours, Coober Pedy tours, mining experience, authentic mining, underground attractions, South Australia tours"
        canonical="/underground-tours"
        ogImage="/og-umoona.jpg"
        schemaData={schemaData}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={tours.images[1]}
            alt="Underground Opal Mine Tours at Umoona"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-4xl">
            <Badge variant="secondary" className="mb-6 bg-orange-500/20 text-orange-300 border border-orange-400/30 px-4 py-2">
              ⛏️ Since 1920s • Authentic Underground Experience
            </Badge>
            
            <h1 className="text-4xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Guided Underground
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-300">
                Opal Mine Tours
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Umoona Opal Mine first worked in the 1920s… and today we take people through this 
              Coober Pedy mine to see (and understand) how opal is formed and mined.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              {tourBookingEnabled && (
                <Button asChild size="lg" className="mining-cta font-bold px-8 py-4 text-lg">
                  <Link to="/book-tour">
                    Book Underground Tour
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              )}
              <Button 
                asChild
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-4 text-lg backdrop-blur-sm"
              >
                <Link to="/free-museum">
                  Explore Free Museum
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PROMINENT Tour Schedule & Pricing */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-red-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                🕒 Tour Schedule & Pricing
              </h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Schedule */}
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <h3 className="text-2xl font-bold text-blue-900 mb-4 flex items-center">
                  <Clock className="w-6 h-6 mr-3" />
                  Tour Times
                </h3>
                <div className="space-y-3">
                  <div className="text-xl font-bold text-blue-800">
                    10am, 2pm and 4pm Daily
                  </div>
                  {tourSchedule.restrict_monday_friday && (
                    <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3">
                      <p className="text-yellow-800 font-semibold">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        EXCEPT MONDAYS & FRIDAYS when only the 4pm tour runs
                      </p>
                    </div>
                  )}
                  {tourSchedule.restrict_monday_friday && tourSchedule.overrides?.some(o => o.enabled) && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                      <p className="text-green-800 text-sm">
                        During certain periods, additional tour times may be available on Mondays and Fridays. Select your date to see available times.
                      </p>
                    </div>
                  )}
                  {openingHours.closed_on_tuesdays && (
                    <div className="bg-red-100 border border-red-300 rounded-lg p-3 mt-3">
                      <p className="text-red-800 font-semibold">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        Closed on Tuesdays
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                <h3 className="text-2xl font-bold text-green-900 mb-4 flex items-center">
                  <DollarSign className="w-6 h-6 mr-3" />
                  Tour Pricing
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold text-green-800">Adults</span>
                    <span className="font-bold text-green-900">$15.50</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold text-green-800">Children</span>
                    <span className="font-bold text-green-900">$8.50</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold text-green-800">Family</span>
                    <span className="font-bold text-green-900">from $36.00</span>
                  </div>
                  <div className="pt-2 text-sm text-green-700">
                    Coach group rates available
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Information Box */}
            <div className="mt-8 bg-purple-50 rounded-xl p-6 border-2 border-purple-200">
              <div className="text-center">
                <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center justify-center">
                  <Info className="w-5 h-5 mr-2" />
                  Booking Information
                </h3>
                <p className="text-lg text-purple-800 mb-4">
                  See our shop staff 10 minutes before tour time to book a ticket, or pre-book online. 
                  <strong className="block mt-2">Limited spaces available for each tour time.</strong>
                </p>
                {tourBookingEnabled && (
                  <Button asChild className="mining-cta">
                    <Link to="/book-tour">
                      Pre-Book Online Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Experience Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What You'll Experience
            </h2>
            <p className="text-xl text-gray-600">
              Every tour includes these incredible experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-6">
              <CardContent className="space-y-4 p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Mountain className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-center">Original Mine Workings</h3>
                <p className="text-gray-600 text-center">
                  Explore authentic tunnels where opals were actually mined decades ago
                </p>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardContent className="space-y-4 p-0">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-center">Expert Guides</h3>
                <p className="text-gray-600 text-center">
                  Learn from locals with decades of mining experience and fascinating stories
                </p>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardContent className="space-y-4 p-0">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Thermometer className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-center">Comfortable Temperature</h3>
                <p className="text-gray-600 text-center">
                  Enjoy consistent 22-24°C underground temperature year-round
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Underground Tour Gallery */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Underground Tour Gallery
            </h2>
            <p className="text-xl text-gray-600">
              See what awaits you in our authentic underground mine tours
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.images.map((image, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg">
                <img 
                  src={image}
                  alt={`Underground mine tour experience ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="font-semibold">Underground Experience</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            {tourBookingEnabled && (
              <Button asChild size="lg" className="mining-cta">
                <Link to="/book-tour">
                  Experience It Yourself
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">
            Important Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-6">
              <CardContent className="space-y-4 p-0">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  What to Bring
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Comfortable closed-toe shoes (essential)</li>
                  <li>• Light jacket or sweater</li>
                  <li>• Camera for photos</li>
                  <li>• Sense of adventure!</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardContent className="space-y-4 p-0">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-blue-600" />
                  Booking Policy
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Advance booking recommended</li>
                  <li>• Group discounts available (10+ people)</li>
                  <li>• All ages welcome (child supervision required)</li>
                  <li>• There are 15 steep steps to enter the mine</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Alert className="mt-8 border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Please note:</strong> Our underground mine tour is not wheelchair accessible due to 15 steep steps and uneven underground terrain. 
              Public areas and the museum are wheelchair accessible.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready to Explore Underground?
              </h2>
              <p className="text-xl text-amber-100 max-w-2xl mx-auto">
                Contact us to learn more about our underground tours and plan your visit to this incredible piece of Australian mining heritage.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {tourBookingEnabled && (
                <Button 
                  asChild
                  size="lg"
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold px-8 py-4 text-lg"
                >
                  <Link to="/book-tour">
                    Book Your Tour Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              )}
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/free-museum">
                  Visit Free Museum
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UndergroundToursPage;